/*
 * The pipeline's column rules and the Quotes page's top cards (owner, 2026-10-29). Each column (stage) can hide leads that sat
 * in it too long, move them on after a number of days, and say when to follow up. The top cards follow Jobber: the quotes by
 * status, then the past 30 days next to the 30 before (conversion rate, sent, converted).
 */
import type { QuoteStatus } from "./billing.js";

const DAY = 86_400_000;

export interface StageRules {
  /** Leads that sat here this many days drop off the board (they are kept and "Show older" brings them back). */
  hideAfterDays: number | null;
  /** Leads that sat here this many days move to moveToStageId on their own. */
  moveAfterDays: number | null;
  moveToStageId: string | null;
  /** Follow up this many days after a lead lands here. Past it the card turns warning, then critical. */
  followUpDays: number | null;
}

export const NO_STAGE_RULES: StageRules = { hideAfterDays: null, moveAfterDays: null, moveToStageId: null, followUpDays: null };

/** The rules as stored (jsonb), with anything missing or broken read as off. */
export function stageRulesOf(raw: unknown): StageRules {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const days = (v: unknown) => (typeof v === "number" && Number.isInteger(v) && v > 0 && v <= 3650 ? v : null);
  const moveTo = typeof r.moveToStageId === "string" && r.moveToStageId ? r.moveToStageId : null;
  const moveAfter = days(r.moveAfterDays);
  return {
    hideAfterDays: days(r.hideAfterDays),
    moveAfterDays: moveTo ? moveAfter : null,
    moveToStageId: moveAfter ? moveTo : null,
    followUpDays: days(r.followUpDays),
  };
}

/** Whole days a lead has sat in its column. */
export const daysInStage = (stageChangedAt: string | Date, now: Date): number =>
  Math.floor((now.getTime() - new Date(stageChangedAt).getTime()) / DAY);

export const hiddenByRules = (rules: StageRules, stageChangedAt: string | Date, now: Date): boolean =>
  rules.hideAfterDays !== null && daysInStage(stageChangedAt, now) >= rules.hideAfterDays;

export const dueToMove = (rules: StageRules, stageChangedAt: string | Date, now: Date): boolean =>
  rules.moveAfterDays !== null && rules.moveToStageId !== null && daysInStage(stageChangedAt, now) >= rules.moveAfterDays;

export type FollowUpLook = "ok" | "warning" | "critical";

/** When to follow up and how late it is: due today is warning, 2 or more days past is critical. */
export function followUp(
  rules: StageRules,
  stageChangedAt: string | Date,
  now: Date,
): { due: Date; daysLate: number; look: FollowUpLook } | null {
  if (rules.followUpDays === null) return null;
  const due = new Date(new Date(stageChangedAt).getTime() + rules.followUpDays * DAY);
  const daysLate = Math.floor((now.getTime() - due.getTime()) / DAY);
  return { due, daysLate, look: daysLate >= 2 ? "critical" : daysLate >= 0 ? "warning" : "ok" };
}

// Top cards -----------------------------------------------------------------------------------------------------------

export interface QuoteForStats {
  status: QuoteStatus;
  totalCents: number;
  sentAt: string | null;
  approvedAt: string | null;
}

export interface Window {
  count: number;
  cents: number;
}

export interface QuoteStats {
  /** Every quote by status now. */
  counts: Record<QuoteStatus, number>;
  /** Sent in the past 30 days, and in the 30 days before that. */
  sent: Window;
  sentBefore: Window;
  /** Approved (or made into a job) in the past 30 days, and the 30 before. */
  converted: Window;
  convertedBefore: Window;
  /** Of the quotes sent in the window, the percent approved so far. Null when none were sent. */
  rate: number | null;
  rateBefore: number | null;
}

const won = (s: QuoteStatus) => s === "approved" || s === "converted";

export function quoteStats(quotes: QuoteForStats[], now: Date): QuoteStats {
  const end = now.getTime();
  const in30 = (at: string | null, back: number) => {
    if (!at) return false;
    const t = new Date(at).getTime();
    return t <= end - back * DAY && t > end - (back + 30) * DAY;
  };
  const sum = (list: QuoteForStats[]): Window => ({ count: list.length, cents: list.reduce((s, q) => s + q.totalCents, 0) });
  const rateOf = (list: QuoteForStats[]) =>
    list.length ? Math.round((list.filter((q) => won(q.status)).length / list.length) * 100) : null;
  const counts = { draft: 0, sent: 0, approved: 0, converted: 0, declined: 0, expired: 0 } as Record<QuoteStatus, number>;
  for (const q of quotes) counts[q.status] += 1;
  const sentNow = quotes.filter((q) => in30(q.sentAt, 0));
  const sentBefore = quotes.filter((q) => in30(q.sentAt, 30));
  return {
    counts,
    sent: sum(sentNow),
    sentBefore: sum(sentBefore),
    converted: sum(quotes.filter((q) => won(q.status) && in30(q.approvedAt, 0))),
    convertedBefore: sum(quotes.filter((q) => won(q.status) && in30(q.approvedAt, 30))),
    rate: rateOf(sentNow),
    rateBefore: rateOf(sentBefore),
  };
}
